// TODO: Examine why placing the stylesheet after App.vue import breaks the layout
import './ui/css/style.scss';

import { gambits } from '@/core/gambits';
import { createLogger, detectLoggingLevel } from '@/core/helpers/logger.helper';
import hoverDirective from '@/directives/hover.directive';
import loadingDirective from '@/directives/loading.directive';
import tooltipDirective from '@/directives/tooltip.directive';
import * as Sentry from '@sentry/vue';
import { createHead } from '@unhead/vue';
import { GesturePlugin } from '@vueuse/gesture';
import { createPinia } from 'pinia';
import { createApp } from 'vue';
import { createVfm } from 'vue-final-modal';
import Toast from 'vue-toastification';
import Vue3TouchEvents, { type Vue3TouchEventsOptions } from 'vue3-touch-events';
import App from './App.vue';
import { hasPendingHandoff, redeemInstallHandoff } from './core/domain/auth/installHandoff';
import { initializePushNotifications, listenForNotificationClicks } from './core/domain/notification/pushNotifications';
import { raiseToastsToTopLayer } from './core/helpers/toast-layer.helper';
import { lockPhonePwaPortrait } from './core/helpers/ui.helper';
import router from './router';
import { useInstallPrompt } from './views/composables/hooks/useInstallPrompt';

import { version } from '../package.json';

export const RELEASE_VERSION = version;

lockPhonePwaPortrait();

// The browser offers its install prompt once, early in the page's life, so it is caught here rather than
// by whichever screen with a pin happens to mount first.
useInstallPrompt().init();

// iOS Safari only applies `:active` when a touch listener exists, so press
// feedback on non-button elements silently does nothing without this.
document.addEventListener('touchstart', () => {}, { passive: true });

const { search, hostname } = window.location;

if (search.includes('visual-test')) {
  window.isVisualTest = true;
}

if (search.includes('automation-mock')) {
  window.isAutomation = true;
}

if (search.includes('init_test_accounts') && hostname === 'localhost') {
  fetch('/v1/users/initialize_test_accounts');
  fetch('/v1/clubs/initialize_test_accounts');
}

if (search.includes('init_test_accounts') && hostname === 'staging.gambits.com') {
  fetch('https://stagingapi.gambits.com/v1/users/initialize_test_accounts');
  fetch('https://stagingapi.gambits.com/v1/clubs/initialize_test_accounts');
}

const logger = createLogger('Gambits');

detectLoggingLevel();

const app = createApp(App);
const head = createHead();
const sentryConfig = {
  dsn: import.meta.env.VITE_SENTRY_DSN,
  tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_RATE) || 0.1,
  tracePropagationTargets: import.meta.env.VITE_SENTRY_PROPAGATION_TARGETS || ['localhost'],
  replaysSessionSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_RATE) || 0,
  replaysOnErrorSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ERROR_RATE) || 1.0,
};

if (sentryConfig?.dsn) {
  Sentry.init({
    app,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Replays are only recorded around errors, so they must never carry readable user content.
      Sentry.replayIntegration({ maskAllText: true, maskAllInputs: true, blockAllMedia: true }),
    ],
    ...sentryConfig,
  });
}

app.use(head);
app.use(createPinia());
app.use(router);
app.use(GesturePlugin);

app.use(loadingDirective);
app.use(tooltipDirective);
app.directive('hover', hoverDirective);

app.use<Vue3TouchEventsOptions>(Vue3TouchEvents, {
  disableClick: true,
});

const vfm = createVfm();
app.use(vfm);

const toastContainer = document.createElement('div');
toastContainer.role = 'dialog';
toastContainer.ariaModal = 'true';
toastContainer.id = 'toast-container';
document.body.appendChild(toastContainer);
raiseToastsToTopLayer(toastContainer);

app.use(Toast, {
  container: toastContainer,
  position: 'top-center',
  transition: 'Vue-Toastification__fade',
  timeout: 5000,
  closeOnClick: true,
  draggable: false,
  hideProgressBar: true,
  closeButton: false,
  icon: false,
  rtl: false,
  maxToasts: 3,
  filterBeforeCreate: (toast: any, toasts: any[]) => {
    if (toasts.some((t: any) => t.content === toast.content)) {
      return false;
    }
    return toast;
  },
});

// Redeeming before mount keeps the router from seeing a signed-out state and
// bouncing a freshly installed app to the auth screen. Capped, because a first
// launch on a cold network would otherwise hold a blank screen for the full
// request timeout.
if (hasPendingHandoff()) {
  const mountBudget = new Promise((resolve) => setTimeout(resolve, 4000));
  void Promise.race([redeemInstallHandoff(), mountBudget]).finally(() => app.mount('#Gambits'));
} else {
  app.mount('#Gambits');
}

void gambits.authService.ensureHandoffCookie();

logger.info(`Release ${RELEASE_VERSION}`);

void initializePushNotifications();
listenForNotificationClicks(router);
